import actionTypes from './actionTypes';
import GraphSource from '../../sources/graph';
import { clone } from 'ramda';
import { setGroupElements, setAllMapElements } from './mapElements';
import { addNotification } from './notification';
import { MAP_ELEMENT_TYPES, notificationTypes } from '../../constants';
import translations from '../../translations';
import { getParents, getParentOffset } from '../../components/graph/utils';

export const setSelectedMapElementID = payload => ({
    type: actionTypes.GRAPH_SET_SELECTED_ELEMENT_ID,
    payload,
});

export const resetSelections = () => ({
    type: actionTypes.GRAPH_RESET_SELECTIONS,
});

export const setMultiSelectedMapElementID = payload => ({
    type: actionTypes.GRAPH_SET_SELECTED_ELEMENT_IDS,
    payload,
});

export const setHoveringMapElementID = payload => ({
    type: actionTypes.GRAPH_SET_HOVER_MAP_ID,
    payload,
});

export const setHoveringGroupElementID = payload => ({
    type: actionTypes.GRAPH_SET_HOVER_GROUP_ID,
    payload,
});

export const setDraggingOverGroupElementID = payload => ({
    type: actionTypes.GRAPH_SET_DRAG_OVER_GROUP_ID,
    payload,
});

export const ungroup = ({ flowId, editingToken, selectedGroupElementId }) => async (dispatch, getState) => {
    const { mapElements, groupElements, currentUser } = getState();
    const { tenantId, authenticationToken } = currentUser;

    let newGroupElements = clone(groupElements);
    let newMapElements = clone(mapElements);

    const selectedGroup = newGroupElements.find(el => el.id === selectedGroupElementId);
    const childGroupElements = newGroupElements.filter(el => el.groupElementId === selectedGroupElementId);
    const childMapElements = newMapElements.filter(el => el.groupElementId === selectedGroupElementId);

    // remove all child group elements from the selected group
    childGroupElements.forEach((group) => {
        group.groupElementId = selectedGroup.groupElementId;
        group.x += selectedGroup.x;
        group.y += selectedGroup.y;

        newGroupElements = [...newGroupElements.filter(el => el.id !== group.id), group];
    });

    // remove all child map elements from the selected group
    childMapElements.forEach((map) => {
        map.groupElementId = selectedGroup.groupElementId;
        map.x += selectedGroup.x;
        map.y += selectedGroup.y;

        newMapElements = [...newMapElements.filter(el => el.id !== map.id), map];
    });

    // save child changes to engine
    await GraphSource.save(
        null,
        {
            id: { id: flowId },
            mapElements: newMapElements,
            groupElements: newGroupElements,
        },
        tenantId,
        authenticationToken,
    );

    // delete the selected group element
    await GraphSource.deleteGroupElement(
        selectedGroupElementId,
        flowId,
        editingToken,
        tenantId,
        authenticationToken,
    );

    newGroupElements = newGroupElements.filter(group => group.id !== selectedGroupElementId);

    dispatch(resetSelections());
    dispatch(setAllMapElements(newMapElements, flowId));
    dispatch(setGroupElements(newGroupElements, flowId));

};

export const groupSelectedElements = ({ flowId, editingToken, x, y, width, height }) => async (dispatch, getState) => {
    const { mapElements, groupElements, graphEditor, currentUser } = getState();
    const { selectedElementIds } = graphEditor;
    const { tenantId, authenticationToken } = currentUser;

    let newGroupElements = clone(groupElements);
    let newMapElements = clone(mapElements);

    // check that all selected elements have the same parent
    const selectedElements = selectedElementIds.map(id => newMapElements.find(el => el.id === id) || newGroupElements.find(el => el.id === id));
    const sameParent = selectedElements.every(el => el.groupElementId === selectedElements[0].groupElementId);
    if (!sameParent) {
        // the selected elements must all share the same groupElementId
        dispatch(addNotification({
            type: notificationTypes.ERROR,
            message: translations.create_group_parents_error,
            isPersistent: true,
        }));
        return;
    }

    // save the new group
    const newGroupParentOffset = getParentOffset(getParents(selectedElements[0], newGroupElements));
    const newGroup = await GraphSource.saveMapElement(
        {
            x: x - newGroupParentOffset.x,
            y: y - newGroupParentOffset.y,
            width,
            height,
            elementType: MAP_ELEMENT_TYPES.group,
            developerName: 'New group',
            groupElementId: selectedElements[0].groupElementId,
        },
        flowId,
        editingToken,
        MAP_ELEMENT_TYPES.group,
        tenantId,
        authenticationToken,
    );
    newGroupElements.push(newGroup);

    // update selected elements
    selectedElementIds.forEach((elementId) => {
        const isGroup = newGroupElements.some(el => el.id === elementId);
        const element = isGroup ?
            newGroupElements.find(el => el.id === elementId) :
            newMapElements.find(el => el.id === elementId);
        element.groupElementId = newGroup.id;
        element.x -= newGroup.x;
        element.y -= newGroup.y;
        if (isGroup) {
            newGroupElements = [...newGroupElements.filter(el => el.id !== elementId), element];
        } else {
            newMapElements = [...newMapElements.filter(el => el.id !== elementId), element];
        }
    });

    GraphSource.save(
        null,
        {
            id: { id: flowId },
            mapElements: newMapElements,
            groupElements: newGroupElements,
        },
        tenantId,
        authenticationToken,
    );

    dispatch(setGroupElements(newGroupElements, flowId));
    dispatch(setAllMapElements(newMapElements, flowId));
    dispatch(setSelectedMapElementID(newGroup.id));
};

export const setDraggingData = payload => ({
    type: actionTypes.GRAPH_SET_DRAGGING_DATA,
    payload,
});

export const setContextMenuData = payload => ({
    type: actionTypes.GRAPH_SET_CONTEXT_MENU_DATA,
    payload,
});
